import { observer } from "mobx-react";
import { useEffect } from "react";
import adminStore from "../../../../stores/admin/adminStore";
import { Button, DatePicker, Drawer, Flex, Form, Input, Modal, Select, Table, Tag } from "antd";
import DropdownActions from "../../../../components/DropdownActions";
import { Gear, InfoCircle, PlusCircle, Trash2, FileText, Calendar2 } from "react-bootstrap-icons";
import dayjs from "dayjs";
import Utils from "../../../../utils/Utils";
import styles from "./index.module.scss";
import { useTabs } from "../../../../hooks/UseTabs";
import { useQueryParams, StringParam, NumberParam, withDefault } from "use-query-params";
import { useNavigate } from "react-router";

const AdminClientsView = observer(() => {
  const [form] = Form.useForm();
  const tabs = useTabs();
  const nav = useNavigate();

  const [query, setQuery] = useQueryParams({
    perPage: withDefault(NumberParam, 15),
    page: withDefault(NumberParam, 1),
    search: StringParam,
    createdAtFrom: StringParam,
    createdAtTo: StringParam,
    expirationDateFrom: StringParam,
    expirationDateTo: StringParam,
    tariffId: StringParam,
    dealsCreatedAtFrom: StringParam,
    dealsCreatedAtTo: StringParam,
  });

  useEffect(() => {
    if (!adminStore.licenses.lookups?.tariffs?.length) {
      adminStore.licenses.findLookups();
    }
    adminStore.findLicenses(query);
    return () => {
      adminStore.licenses.data = undefined;
    };
  }, [query]);

  const applyFilters = (newFilters: Partial<typeof query>) => {
    const filterKeys = [
      "search",
      "createdAtFrom",
      "createdAtTo",
      "expirationDateFrom",
      "expirationDateTo",
      "tariffId",
      "dealsCreatedAtFrom",
      "dealsCreatedAtTo"
    ];

    const isFilterChanged = Object.keys(newFilters).some(
      (key) => filterKeys.includes(key) && newFilters[key as keyof typeof query] !== query[key as keyof typeof query]
    );

    setQuery(
      {
        ...query,
        ...newFilters,
        page: isFilterChanged ? 1 : newFilters.page ?? query.page,
      },
      "replace"
    );
  };

  const onCreate = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const formattedValues = {
        ...values,
        startsAt: values.startsAt ? dayjs(values.startsAt).toISOString() : undefined,
        expiresAt: values.expiresAt ? dayjs(values.expiresAt).toISOString() : undefined,
      };

      if (adminStore.licenses.state === "Create") {
        await adminStore.licenses.onCreate(formattedValues);
      }

      if (adminStore.licenses.state === "Edit") {
        await adminStore.licenses.onUpdate(formattedValues);
        tabs.remove("licenseId");
      }
      
      adminStore.findLicenses(query);
    } catch (error) {
      console.error(error);
    }
  };

  const onCancel = () => {
    adminStore.licenses.state = "None";
    tabs.remove("licenseId");
    adminStore.licenses.historyState = "None";
  };

  const onFindHistory = async (id: string) => {
    await adminStore.licenses.onFindHistory(id);
  };

  const handleEditNote = (user: any) => {
    form.setFieldsValue(user);
    adminStore.licenses.noteState = "Edit";
  };

  const handleSaveNote = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      await adminStore.licenses.updateNote(values.id, values);
      adminStore.findLicenses(query);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancelNote = () => {
    adminStore.licenses.noteState = "None";
  };

  return (
    <div className={styles.area}>
      <Flex gap={24} className={styles.filters}>
        <Input
          value={query.search ?? ""}
          onChange={(e) => applyFilters({ search: e.target.value, page: 1 })}
          style={{ width: 300 }}
          placeholder="Поиск (ФИО, телефон, email, лицензия)"
          allowClear
        />

        <Select
          allowClear
          style={{ width: 220 }}
          placeholder="Выберите тариф"
          value={query.tariffId ?? undefined}
          onChange={(val) => applyFilters({ tariffId: val ?? null })}
          options={
            adminStore.licenses.lookups?.tariffs.map((t) => ({
              label: t.name,
              value: t.id,
            })) || []
          }
        />

        <DatePicker.RangePicker
          style={{ width: 300 }}
          placeholder={["Лицензия с", "Лицензия до"]}
          allowClear
          suffixIcon={<Calendar2 size={20} />}
          value={query.expirationDateFrom ? [dayjs(query.expirationDateFrom), dayjs(query.expirationDateTo)] : null}
          format="DD.MM.YYYY"
          onChange={(val) => {
            if (!val) {
              applyFilters({ expirationDateFrom: null, expirationDateTo: null });
              return;
            }
          
            if (val[0] && val[1]) {
              applyFilters({
                expirationDateFrom: val[0].startOf("day").toISOString(),
                expirationDateTo: val[1].endOf("day").toISOString(),
              });
            }
          }}
        />
        <DatePicker.RangePicker
          style={{ width: 300 }}
          placeholder={["Регистр. с", "Регистр. до"]}
          allowClear
          suffixIcon={<Calendar2 size={20} />}
          value={query.createdAtFrom ? [dayjs(query.createdAtFrom), dayjs(query.createdAtTo)] : null}
          format="DD.MM.YYYY"
          onChange={(val) => {
            if (!val) {
              applyFilters({ createdAtFrom: null, createdAtTo: null });
              return;
            }
          
            if (val[0] && val[1]) {
              applyFilters({
                createdAtFrom: val[0].startOf("day").toISOString(),
                createdAtTo: val[1].endOf("day").toISOString(),
              });
            }
          }}
        />
        <DatePicker.RangePicker
          style={{ width: 300 }}
          placeholder={["Сделки с", "Сделки до"]}
          allowClear
          suffixIcon={<Calendar2 size={20} />}
          value={
            query.dealsCreatedAtFrom
              ? [
                  dayjs(query.dealsCreatedAtFrom),
                  dayjs(query.dealsCreatedAtTo),
                ]
              : null
          }
          format="DD.MM.YYYY"
          onChange={(val) => {
            if (!val) {
              applyFilters({
                dealsCreatedAtFrom: null,
                dealsCreatedAtTo: null,
              });
              return;
            }
          
            if (val[0] && val[1]) {
              applyFilters({
                dealsCreatedAtFrom: val[0].startOf("day").toISOString(),
                dealsCreatedAtTo: val[1].endOf("day").toISOString(),
              });
            }
          }}
        />
      </Flex>
      <Table
        className={styles.clientsTable}
        dataSource={adminStore.licenses.data?.data}
        rowKey={(e) => e.id}
        loading={!adminStore.licenses.data}
        scroll={{ y: "calc(100vh - 324px)", x: "max-content" }}
        sticky
        columns={[
          {
            title: "Клиент",
            width: 170,
            render: (_, i) => {
              return (
                <div
                  className={styles.clientName}
                  onClick={() => nav(i.id)}
                >
                  {[i.profile?.lastName, i.profile?.firstName].join(" ")}
                </div>
              );
            },
          },
          {
            title: "Email",
            dataIndex: "email",
            width: 150,
            ellipsis: true,
          },
          {
            title: "Сделок",
            dataIndex: "dealsCount",
            width: 90,
            align: "center",
            sorter: false,
            render: (_, i) => i.dealsCount ?? 0,
          },
          {
            title: "Телефон",
            width: 150,
            render: (_, i) => (i.profile?.phoneNumber ? i.profile?.phoneNumber : "-"),
          },
          {
            title: "Активный функционал",
            width: 260,
            render: (_, i) => i.activeFeatures?.length ? (
              <div className={styles.activeFeatures}>
                {i.activeFeatures.map(feature => <Tag className={styles.featureTag} color="green" key={feature.code}>{feature.title}</Tag>)}
              </div>
            ) : "—",
          },
          {
            title: "Срок лицензии",
            width: 100,
            align: "center",
            render: (_, i) => {
              if (i.license) {
                const isExpired = dayjs(i.license.expiresAt).isBefore(dayjs());
                const color = isExpired ? "var(--fury)" : "var(--night900)";
                return <div style={{ color, fontWeight: 500 }}>{`${Utils.format(i.license.expiresAt, "DD.MM.YYYY")}`}</div>;
              }

              return "-";
            },
          },
          {
            title: "Дата регистрации",
            width: 140,
            align: "center",
            render: (_, i) => Utils.format(i.createdAt, "DD.MM.YYYY"),
          },
          {
            title: "Промокод",
            width: 100,
            align: "center",
            render: (_, i) => (
              <Flex wrap>
                {i.promocodes?.map((e) => (
                  <div className={styles.code} key={e.id}>
                    {e.code}
                  </div>
                ))}
              </Flex>
            ),
          },
          {
            title: "Купон",
            width: 80,
            align: "center",
            render: (_, i) => (i.coupon ? i.coupon.value : "-"),
          },
          {
            title: "Примечание",
            width: 260,
            render: (_, i) => <div className={styles.note}>{i.note || "-"}</div>,
          },
          {
            title: " ",
            width: 70,
            render: (_, i) => (
              <DropdownActions
                actions={[
                  {
                    icon: <InfoCircle />,
                    label: "Подробности",
                    onClick: () => nav(i.id),
                  },
                  {
                    icon: <FileText />,
                    label: "Редактировать примечание",
                    onClick: () => handleEditNote(i),
                  },
                  {
                    icon: <InfoCircle />,
                    label: "История лицензий",
                    onClick: () => onFindHistory(i.id),
                  },
                  {
                    icon: <Gear />,
                    disabled: !i.license,
                    label: "Редактировать лицензию",
                    onClick: async () => {
                      await adminStore.licenses.findLookups();
                      await adminStore.licenses.onRead(i.license?.id);

                      const limits = adminStore.licenses.item?.limits.map((limit) => ({
                        key: limit.key.value,
                        value: limit.value,
                      }));

                      form.setFieldsValue({
                        ...adminStore.licenses.item,
                        tariffId: adminStore.licenses.item?.tariff.id,
                        limits,
                      });
                      // tabs.set("licenseId", i.license.id);
                      // form.setFieldsValue({
                      //   userId: i.id,
                      //   name: i.license.name,
                      //   expirationDate: i.license.expirationDate,
                      //   policyIds: i.license.policies.map((e) => e.id),
                      // });
                    },
                  },
                  {
                    icon: i.license ? <Trash2 /> : <PlusCircle />,
                    label: i.license ? "Удалить лицензию" : "Добавить лицензию",
                    onClick: async () => {
                      if (i.license) {
                        Modal.confirm({
                          title: "Вы уверены?",
                          okText: "Удалить",
                          cancelText: "Отменить",
                          onOk: async () => {
                            await adminStore.licenses.onDelete(i.license?.id);
                            adminStore.findLicenses(query);
                          },
                        });
                      } else {
                        await adminStore.licenses.findLookups();
                        form.setFieldValue("userId", i.id);
                        adminStore.licenses.state = "Create";
                        form.setFieldValue("startsAt", new Date());
                      }
                    },
                  },
                ]}
              />
            ),
          },
        ]}
        footer={() =>
          adminStore.licenses.data?.meta.total
            ? `Показано ${Math.min((query.page - 1) * query.perPage + 1, adminStore.licenses.data.meta.total)}–${Math.min(
                query.page * query.perPage,
                adminStore.licenses.data.meta.total
              )} из ${adminStore.licenses.data.meta.total}`
            : null
        }
        pagination={{
          current: query.page,
          pageSize: query.perPage,
          total: adminStore.licenses.data?.meta.total,
          showSizeChanger: true,
          pageSizeOptions: ["15", "20", "50"],
        }}
        onChange={({ current, pageSize }, _, sorter) => {
          applyFilters({ page: current, perPage: pageSize });
        }}
      />
      <Modal
        title={adminStore.licenses.state === "Create" ? "Назначить лицензию" : "Редактировать лицензию"}
        open={adminStore.licenses.state !== "None"}
        onOk={onCreate}
        centered
        width={600}
        onCancel={onCancel}
        okButtonProps={{ disabled: adminStore.licenses.isLoading }}
        okText={adminStore.licenses.state === "Create" ? "Добавить" : "Сохранить"}
      >
        <Form form={form} layout="vertical">
          <Form.Item name={"userId"} hidden />
          <Form.Item label="Выберите тариф" name="tariffId" rules={[{ required: true, message: " " }]}>
            <Select options={adminStore.licenses.lookups?.tariffs.map((e) => ({ value: e.id, label: e.name }))} />
          </Form.Item>
          <Form.Item
            name={"startsAt"}
            label="Укажите срок начала"
            rules={[{ required: true, message: " " }]}
            getValueProps={(value) => {
              return { value: value ? dayjs(value) : undefined };
            }}
          >
            <DatePicker format={"DD-MM-YYYY"} suffixIcon={<Calendar2 size={18} />} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name={"expiresAt"}
            label="Укажите срок окончания"
            rules={[{ required: true, message: " " }]}
            getValueProps={(value) => {
              return { value: value ? dayjs(value) : undefined };
            }}
          >
            <DatePicker format={"DD-MM-YYYY"} suffixIcon={<Calendar2 size={18} />} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name={"description"} label="Описание">
            <Input />
          </Form.Item>
          <Form.List name="limits">
            {(fields, { add, remove }) => {
              const selectedKeys =
                form
                  .getFieldValue("limits")
                  ?.map((l: any) => l?.key)
                  .filter(Boolean) || [];

              const availableLimitOptions =
                adminStore.licenses.lookups?.limits
                  .filter((lim) => !selectedKeys.includes(lim.value)) // filter out already selected keys
                  .map((lim) => ({
                    label: lim.title,
                    value: lim.value,
                  })) || [];

              return (
                <>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Ограничения</div>
                  {fields.map((field) => (
                    <div key={field.key} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <Form.Item {...field} name={[field.name, "key"]} rules={[{ required: true, message: " " }]} style={{ width: "100%" }}>
                        <Select
                          placeholder="Тип"
                          options={adminStore.licenses.lookups?.limits.map((lim) => ({
                            label: lim.title,
                            value: lim.value,
                            disabled: selectedKeys.includes(lim.value) && form.getFieldValue(["limits", field.name, "key"]) !== lim.value,
                          }))}
                        />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        name={[field.name, "value"]}
                        rules={[{ required: true, message: " " }]}
                        style={{ width: 120, minWidth: 120 }}
                      >
                        <Input type="number" placeholder="Значение" />
                      </Form.Item>

                      <Button onClick={() => remove(field.name)} danger type="text">
                        Удалить
                      </Button>
                    </div>
                  ))}
                  {availableLimitOptions.length > 0 && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block>
                        Добавить ограничение
                      </Button>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.List>
        </Form>
      </Modal>
      <Modal
        title="Редактировать примечание"
        open={adminStore.licenses.noteState === "Edit"}
        onOk={handleSaveNote}
        onCancel={handleCancelNote}
        okButtonProps={{ disabled: adminStore.licenses.isLoading }}
        okText="Сохранить"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden />
          <Form.Item name="note" label="Примечание">
            <Input.TextArea rows={4} maxLength={10000} showCount placeholder="Введите примечание" />
          </Form.Item>
        </Form>
      </Modal>
      <Drawer open={adminStore.licenses.historyState === "Preview"} title="История лицензий" onClose={onCancel} width={400} footer={null}>
        <div className={styles.history}>
          {adminStore.licenses.history?.map((e) => (
            <div key={e.id} className={styles.box}>
              <div className={styles.title}>{e.name}</div>
              <div style={{ height: 8 }} />
              <div>{e.action.title}</div>
              <div style={{ height: 8 }} />
              <div>{`Дата создания: ${Utils.format(e.createdAt, "DD.MM.YYYY")}`}</div>
              <div>{`Срок окончания: ${Utils.format(e.expiresAt, "DD.MM.YYYY")}`}</div>
              <div style={{ height: 8 }} />
              <Flex wrap gap={8}>
                {e.policiesList.map((i) => (
                  <div key={i} className={styles.policy}>
                    {i}
                  </div>
                ))}
              </Flex>
            </div>
          ))}
        </div>
      </Drawer>
    </div>
  );
});

export default AdminClientsView;
